import { defineStore } from "pinia";
import { ICostumer } from "@/types/costumer";
import axios from "axios";
import {
    costumerDeadlineStartpointUrl,
    costumerDetailUrl,
    costumerRestoreUrl,
    costumersListUrl,
    zoneAssignCostumersUrl,
    zoneDetailUrl,
    zonesListUrl,
} from "@/services/api";
import { useNetworkStore } from "@/stores/network";
import { archive } from "ionicons/icons";
import { IZone } from "@/types/zone";
import { is } from "date-fns/locale";

// You can name the return value of `defineStore()` anything you want,
// but it's best to use the name of the store and surround it with `use`
// and `Store` (e.g. `useUserStore`, `useCartStore`, `useProductStore`)
// the first argument is a unique id of the store across your application
export const useCostumersStore = defineStore("costumers", {
    state: () => ({
        loading: false as boolean,
        archivedLoading: false as boolean,
        costumers: [] as ICostumer[],
        archivedCostumers: [] as ICostumer[],
        loadingZones: false as boolean,
        zones: [] as IZone[],
    }),
    persist: true,
    getters: {
        isLoading: (state) => state.loading,
        isArchivedLoading: (state) => state.archivedLoading,
        isLoadingZones: (state) => state.loadingZones,
        getCostumers: (state) => state.costumers,
        getArchivedCostumers: (state) => state.archivedCostumers,
        getZones: (state) => state.zones,
        getCustomerById: (state) => (id: number) => state.costumers.find((costumer) => costumer.id === id),
        getZoneById: (state) => (id: number) => state.zones.find((zone) => zone.id === id),
        getCostumerByZone: (state) => (zoneId: number) => {
            return state.costumers.filter((costumer) => costumer.zone === zoneId);
        },
    },
    actions: {
        async loadZones() {
            this.loadingZones = true;
            const response = await axios.get(zonesListUrl);
            if (response.status === 200) {
                this.zones = response.data;
            }
            this.loadingZones = false;
            return response;
        },
        async loadArchivedCostumers() {
            this.archivedLoading = true;
            const response = await axios.get(costumersListUrl, {
                params: {
                    archived: true,
                },
            });
            if (response.status === 200) {
                this.archivedCostumers = response.data;
            }
            this.archivedLoading = false;
            return response;
        },
        async loadCostumers() {
            this.loading = true;
            const response = await axios.get(costumersListUrl);
            if (response.status === 200) {
                this.costumers = response.data;
            }
            this.loading = false;
            return response;
        },
        async reloadCostumer(costumerId: number) {
            this.loading = true;
            const networkStore = useNetworkStore();
            if (networkStore.isOnline) {
                const response = await axios.get(costumerDetailUrl(costumerId));
                if (response.status === 200) {
                    const index = this.costumers.findIndex((c) => c.id === costumerId);
                    if (index >= 0) {
                        this.costumers[index] = response.data;
                    }
                }
                this.loading = false;
                return response;
            }
            this.loading = false;
            return null;
        },
        async createCostumer(costumer: ICostumer) {
            const networkStore = useNetworkStore();
            if (networkStore.isOnline) {
                const response = await axios.post(costumersListUrl, costumer);
                if (response.status === 201) {
                    this.costumers.push(response.data as ICostumer);
                }
                return response;
            }
        },
        async updateCostumer(costumer: ICostumer) {
            const networkStore = useNetworkStore();
            const index = this.costumers.findIndex((c) => c.id === costumer.id);
            if (index >= 0 && networkStore.isOnline) {
                const response = await axios.put(costumerDetailUrl(costumer!.id), costumer);
                if (response.status === 200) {
                    this.costumers[index] = response.data;
                }
                return response;
            }
        },
        async costumerDeadlineStartpoint(costumer: ICostumer) {
            const networkStore = useNetworkStore();
            const index = this.costumers.findIndex((c) => c.id === costumer.id);
            if (index >= 0 && networkStore.isOnline) {
                return await axios.get(costumerDeadlineStartpointUrl(costumer!.id));
            }
        },
        async archiveCostumer(costumerId: number) {
            this.loading = true;
            const networkStore = useNetworkStore();
            const index = this.costumers.findIndex((c) => c.id === costumerId);
            if (index >= 0 && networkStore.isOnline) {
                const response = await axios.delete(costumerDetailUrl(costumerId));
                if (response.status === 204) {
                    this.archivedCostumers.push(this.costumers[index]);
                    this.costumers.splice(index, 1);
                }
                this.loading = false;
                return response;
            }
            this.loading = false;
        },
        async restoreCostumer(costumerId: number) {
            this.archivedLoading = true;
            const networkStore = useNetworkStore();
            const index = this.archivedCostumers.findIndex((c) => c.id === costumerId);
            if (index >= 0 && networkStore.isOnline) {
                const response = await axios.put(costumerRestoreUrl(costumerId));
                if (response.status === 206) {
                    this.archivedCostumers.splice(index, 1);
                    this.costumers.push(response.data as ICostumer);
                }
                this.archivedLoading = false;
                return response;
            }
            this.archivedLoading = false;
        },
        async assignCostumersToZone(costumerIds: number[], zoneId: number) {
            this.loading = true;
            const networkStore = useNetworkStore();
            if (networkStore.isOnline) {
                const response = await axios.post(zoneAssignCostumersUrl(zoneId), {
                    costumers: costumerIds,
                });
                if (response.status === 200) {
                    const response = await axios.get(costumersListUrl);
                    if (response.status === 200) {
                        this.costumers = response.data;
                    }
                }
                this.loading = false;
                return response;
            }
            this.loading = false;
        },
        async createZone(zone: IZone) {
            this.loadingZones = true;
            const networkStore = useNetworkStore();
            if (networkStore.isOnline) {
                const response = await axios.post(zonesListUrl, zone);
                if (response.status === 201) {
                    this.zones.push(response.data as IZone);
                }
                this.loadingZones = false;
                return response;
            }
            this.loadingZones = false;
        },
        async updateZone(zone: IZone) {
            this.loadingZones = true;
            const networkStore = useNetworkStore();
            const index = this.zones.findIndex((z) => z.id === zone.id);
            if (index >= 0 && networkStore.isOnline) {
                const response = await axios.put(zoneDetailUrl(zone.id), zone);
                if (response.status === 200) {
                    this.zones[index] = response.data;
                }
                this.loadingZones = false;
                return response;
            }
            this.loadingZones = false;
        },
    },
});
